import { Temporal } from '@js-temporal/polyfill';

import { birthdayDate, type CalendarItem, localDay, shiftDay } from './calendar';
import type { RecordData } from './model';

/**
 * When each calendar reminder should fire.
 *
 * `reminderList` in `calendar.ts` answers a different question — what is due
 * *now*, for the in-app list. A scheduler needs the future: the instant each
 * reminder fires, computed the same way wherever it is computed. iOS arms local
 * notifications from this rule and the server sends push from it, so the two
 * cannot disagree about when a reminder is due or which reminder it is.
 *
 * Recurring appointments need no expansion here. `recurrence.ts` materialises a
 * series into one record per occurrence at the moment it is created, so every
 * occurrence already is a record with its own `startsAt`.
 */

/**
 * Nine in the morning of the day preparation should begin: one reminder for the
 * whole birthday window rather than a daily nag.
 */
export const birthdayHour = 9;

export type ReminderChannel = 'appointment' | 'birthday';

export type PlannedReminder = {
  recordId: string;
  channel: ReminderChannel;
  /**
   * Identifies this firing, not the record. Moving an event or changing its
   * lead changes the instant and therefore the key, so the moved reminder is a
   * new one and sends again; nothing else does. A sender that writes the key
   * down before sending delivers each reminder exactly once.
   */
  key: string;
  fireAt: string;
  title: string;
  body: string;
};

function appointmentBody(startsAt: string, lead: number, zone: string) {
  const time = Temporal.Instant.from(startsAt)
    .toZonedDateTimeISO(zone)
    .toPlainTime()
    .toString({ smallestUnit: 'minute' });

  return lead > 0 ? `Starts at ${time}, in ${lead} minutes.` : `Starts at ${time}.`;
}

/**
 * Every reminder firing in `[from, until)`, nearest first.
 *
 * No cap is applied. iOS keeps at most 64 pending notification requests and its
 * scheduler truncates to fit; that is a property of the phone, not of the rule,
 * and a server sending push has no such budget.
 */
export function reminderSchedule(
  records: (Pick<RecordData, 'id' | 'kind' | 'value'> & { deleted?: boolean })[],
  { from, until, zone }: { from: Date; until: Date; zone: string },
) {
  const planned: PlannedReminder[] = [],
    today = localDay(from, zone);

  const add = (reminder: Omit<PlannedReminder, 'key'>) => {
    // Half-open: `from` is inside the window and `until` is not. A sender whose
    // next window begins where this one ended therefore sees a reminder landing
    // exactly on the boundary once — excluding both ends would drop it
    // silently, and including both would send it twice.
    if (
      Date.parse(reminder.fireAt) < from.getTime() ||
      Date.parse(reminder.fireAt) >= until.getTime()
    ) {
      return;
    }

    planned.push({
      ...reminder,
      key: `${reminder.recordId}.${reminder.channel}.${reminder.fireAt}`,
    });
  };

  for (const record of records) {
    if (record.kind !== 'calendar' || record.deleted) {
      continue;
    }

    const item = record.value as CalendarItem;

    // Availability windows carry no `cancelled` and never remind, so the guard
    // lives inside the two branches that have one rather than above them.
    if (item.type === 'booking' && !item.cancelled) {
      add({
        recordId: record.id,
        channel: 'appointment',
        fireAt: new Date(Date.parse(item.startsAt) - item.reminderMinutes * 60000).toISOString(),
        title: item.title,
        body: appointmentBody(item.startsAt, item.reminderMinutes, zone),
      });
    }

    if (item.type === 'birthday' && !item.cancelled) {
      // The next one that has not happened yet, which in the last days of
      // December is next year's.
      let date = birthdayDate(item, Number(today.slice(0, 4)));

      if (date < today) {
        date = birthdayDate(item, Number(today.slice(0, 4)) + 1);
      }

      const opens = shiftDay(date, -item.reminderDays);

      add({
        recordId: record.id,
        channel: 'birthday',
        fireAt: new Date(
          Temporal.PlainDateTime.from(
            `${opens}T${String(birthdayHour).padStart(2, '0')}:00`,
          ).toZonedDateTime(zone, { disambiguation: 'later' }).epochMilliseconds,
        ).toISOString(),
        title: item.title,
        body: `Birthday on ${date}. Time to prepare.`,
      });
    }
  }

  return planned.sort((a, b) => a.fireAt.localeCompare(b.fireAt));
}
